import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { getThreatBands, type ThreatBand, type ThreatBands } from './api'

/**
 * The threat filter, shared by the console, the situation board and the threads feed.
 *
 * ## The bands come from the server
 *
 * Their boundaries are derived from the room's deploy threshold, so "High" means 7–8 in a room
 * that deploys at 7 and 5–6 in one that deploys at 5. Every option therefore renders the range
 * alongside the word: a filter that offers "High" without saying which numbers that is asks the
 * operator to trust a label whose meaning is configuration.
 *
 * ## "Not read" is an option, not the bottom of the scale
 *
 * A thread nobody has assessed has no score, so it cannot satisfy any numeric band — selecting
 * "Critical" hides every unassessed thread, including ones that would score 10 if anybody
 * looked. That is the correct query and a dangerous default, which is why:
 *
 *  - `UNREAD` is offered as a peer of the numeric bands, never folded into "Low"; and
 *  - whenever a numeric band is active, the caller is expected to render {@link ExcludedNote}
 *    so the number of unread threads sitting outside the filter stays on screen.
 *
 * A filter that quietly narrows a war room's field of view is the exact failure this feature
 * exists to prevent.
 */

const INK: Record<string, string> = {
  CRITICAL: 'var(--severity-critical)',
  HIGH: 'var(--severity-high)',
  MEDIUM: 'var(--severity-medium)',
  LOW: 'var(--severity-low)',
  UNREAD: 'var(--color-muted-foreground)',
}

/** Console variant — the console defines its own ink tokens. */
const CONSOLE_INK: Record<string, string> = { ...INK, UNREAD: 'var(--graph-unread)' }

export function useThreatBands() {
  const [bands, setBands] = useState<ThreatBands | null>(null)
  useEffect(() => {
    let cancelled = false
    getThreatBands()
      .then((b) => { if (!cancelled) setBands(b) })
      // A filter that cannot load its options must not take the screen down with it. The page
      // still works; it simply cannot narrow, which is the safe direction to fail in.
      .catch(() => { if (!cancelled) setBands(null) })
    return () => { cancelled = true }
  }, [])
  return bands
}

export function ThreatFilter({ bands, value, onChange, tone = 'app', className }: {
  bands: ThreatBands | null
  /** A band code, or '' for no filter. */
  value: string
  onChange: (code: string) => void
  tone?: 'app' | 'console'
  className?: string
}) {
  if (!bands) return null
  const ink = tone === 'console' ? CONSOLE_INK : INK
  const selected = bands.bands.find((b) => b.code === value)

  return (
    <div className={cn('flex flex-wrap items-center gap-1', className)}>
      <span className={cn('mr-1 font-mono text-[9px] tracking-[0.16em] uppercase',
        tone === 'console' ? '' : 'text-muted-foreground')}
            style={tone === 'console' ? { color: 'var(--graph-ink-faint)' } : undefined}>
        Threat
      </span>

      <FilterChip
        active={value === ''}
        tone={tone}
        onClick={() => onChange('')}
        title={`Everything in the window, scored or not — the full ${bands.scaleMin}–${bands.scaleMax} scale plus threads nothing has read.`}
      >
        Any
      </FilterChip>

      {bands.bands.map((band) => (
        <FilterChip
          key={band.code}
          active={value === band.code}
          tone={tone}
          ink={ink[band.code]}
          onClick={() => onChange(value === band.code ? '' : band.code)}
          title={`${band.display} — ${band.note}`}
        >
          {band.display}
        </FilterChip>
      ))}

      {selected ? (
        <span className={cn('ml-1 text-[10px]', tone === 'console' ? '' : 'text-muted-foreground')}
              style={tone === 'console' ? { color: 'var(--graph-ink-faint)' } : undefined}>
          {selected.note}
        </span>
      ) : null}
    </div>
  )
}

function FilterChip({ active, ink, tone, onClick, title, children }: {
  active: boolean
  ink?: string
  tone: 'app' | 'console'
  onClick: () => void
  title: string
  children: React.ReactNode
}) {
  const accent = tone === 'console' ? 'var(--graph-accent)' : 'var(--chocolate)'
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={title}
      className={cn(
        'border px-2 py-1 font-mono text-[9.5px] tracking-[0.1em] uppercase transition-colors',
        !active && 'hover:opacity-80',
      )}
      style={{
        color: active ? accent : (ink ?? (tone === 'console'
          ? 'var(--graph-ink-faint)' : 'var(--color-muted-foreground)')),
        borderColor: active ? accent : 'transparent',
        background: active
          ? `color-mix(in oklab, ${accent} 10%, transparent)`
          : 'transparent',
      }}
    >
      {children}
    </button>
  )
}

/**
 * What the active filter is keeping off the screen.
 *
 * Rendered whenever a NUMERIC band is selected. "Showing 4 of 363" is a fact about pagination;
 * "355 threads nobody has scored are outside this filter" is a fact about the room, and it is
 * the one that stops a narrowed view being mistaken for a quiet one.
 */
export function ExcludedNote({ band, matched, windowTotal, unscored, tone = 'app' }: {
  band: string
  matched: number
  windowTotal: number
  unscored: number
  tone?: 'app' | 'console'
}) {
  if (!band) return null
  const numeric = band !== 'UNREAD'
  const faint = tone === 'console' ? 'var(--graph-ink-faint)' : 'var(--color-muted-foreground)'
  const warn = 'var(--severity-medium)'
  return (
    <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10.5px]"
          style={{ color: faint }}>
      <span>
        {matched.toLocaleString()} of {windowTotal.toLocaleString()} in the window match.
      </span>
      {numeric && unscored > 0 ? (
        <span style={{ color: warn }}>
          {unscored.toLocaleString()} not read are outside this filter — unscored is not low.
        </span>
      ) : null}
    </span>
  )
}

export type { ThreatBand }
